import { cloneTemplate } from '../shared/dom-template';
import { getKioskConfig, isKioskVirtualKeyboardEnabled } from '../shared/kiosk-config';
import { attachAmountVirtualKeyboard, initVirtualKeyboard } from '../shared/virtual-keyboard-core';
import {
    getPledgePaySurchargeAmt,
    getPledgePayCongregationGatewayCount,
} from './pledge-pay-config';
import {
    mgrPledgeModalSyncSummaryVisibility,
    mgrPledgeModalSyncPaymentTableFooter,
    pledgePayBillSyncSummaryColumnLayout,
    pledgePayBillUpdateSummary,
} from './pledge-pay-balance';
import {
    updatePledgePayNowButtonEnabledState,
    updateStatementPledgeSelectionValidation,
    syncPledgePayGatewayTotals,
} from './pledge-pay-amount';
import {
    buildPledgePayTableContext,
    resolvePledgePayTableProfile,
} from './pledge-pay-surface';
import {
    formatCampaignNounPhrase,
    formatCurrency,
    formatSurchargePercent,
    isMatanahAmountPending,
    pledgePayAmountDue,
} from './pledge-pay-utils';
import {
    pledgeUpdateSurchargeSectionRowVisibility,
    updateForceSurchargeRow,
    syncGatewaySurchargePanels,
} from './pledge-pay-surcharge';
import { setPledgePayDetailsExpanded } from './pledge-pay-details';

function createSurchargeAccumulator() {
    return {
        forceSurchargeFound: false,
        forceCampaignsArr: [],
        optionSurchargeFound: false,
        optionCampaignsArr: [],
    };
}

function accumulatePledgeRowStats(pledge, acc) {
    trackSurcharge(pledge, acc);
    const use_amount = pledgePayAmountDue(pledge);
    const isMatanahHold = isMatanahAmountPending(pledge, use_amount);
    return { use_amount, isMatanahHold };
}

function foldPledgeTableRows(pledges, appendRow) {
    const acc = createSurchargeAccumulator();
    let totalAmount = 0;
    let matanahFound = false;
    let matanahCount = 0;

    pledges.forEach((pledge, index) => {
        const stats = accumulatePledgeRowStats(pledge, acc);
        appendRow(pledge, stats, index);
        if (stats.isMatanahHold) {
            matanahFound = true;
            matanahCount++;
        } else {
            totalAmount += stats.use_amount;
        }
    });

    return {
        totalAmount,
        matanahCount,
        matanahFound,
        ...acc,
    };
}

function buildAmountInput({ id, value, max, min = 0, disabled = false }) {
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.step = '0.01';
    inp.className = 'form-control-sm amount_value matanah_amt_entry';
    inp.id = `amount_paid[${id}]`;
    inp.name = 'amount_paid';
    inp.value = formatCurrency(Number(value) || 0);
    inp.max = String(max);
    if (min) inp.min = String(min);
    if (disabled) inp.disabled = true;
    return inp;
}

function buildMatanahSaveGroup(pledgeId) {
    const $group = cloneTemplate('tpl-matanah-save-group');
    $group.attr('id', `matanah_input-${pledgeId}`);
    $group.find('input[name="matanah_amount"]').attr({
        id: `matanah_amount[${pledgeId}]`,
        min: 1,
    });
    $group.find('.matanah-save-btn').attr('data-pledge-id', pledgeId);
    return $group;
}

function parsePledgeDate(p) {
    const raw = p.date_pledged;
    if (!raw) return null;
    const usSlash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(raw).trim());
    if (usSlash) {
        const dt = new Date(Number(usSlash[3]), Number(usSlash[1]) - 1, Number(usSlash[2]));
        return Number.isNaN(dt.getTime()) ? null : dt;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}

function sortPledges(pledges) {
    return [...pledges].sort((a, b) =>
        (parsePledgeDate(a) ?? 0) - (parsePledgeDate(b) ?? 0) ||
        (Number(a.id) || 0) - (Number(b.id) || 0)
    );
}

function buildPledgeRow(pledge, opts = {}) {
    const $tr = cloneTemplate('tpl-pledge-payment-row');
    const use_amount = pledgePayAmountDue(pledge);
    const isMatanahHold = isMatanahAmountPending(pledge, use_amount);
    const amountToPay = isMatanahHold ? pledge.amount : use_amount;

    $tr.attr({
        'data-pledge-id': pledge.id,
        'data-table-name': pledge.table_name || 'pledge',
        'data-amount-to-pay': amountToPay,
        'data-balance-due': use_amount,
        'data-force-surcharge': isForceSurchargePledge(pledge) ? 1 : 0,
        'data-campaign': pledge.campaign || '',
    });

    const resolvedGatewayId = opts.gatewayId != null
        ? Number(opts.gatewayId)
        : Number(pledge.gateway_id);
    if (!Number.isFinite(resolvedGatewayId) || resolvedGatewayId <= 0) {
        throw new Error(`buildPledgeRow: pledge ${pledge.id} missing valid gateway_id`);
    }
    $tr.attr('data-gateway-id', resolvedGatewayId);
    if (opts.showSelect) $tr.find('.pledge-payment-td-select').removeClass('d-none');
    if (opts.showOrder) {
        const orderNum = opts.orderIndex + 1;
        $tr.find('.pledge-payment-td-order').removeClass('d-none').html(
            `<span class="pledge-payment-order-cell"><i class="fa-solid fa-grip-vertical" aria-hidden="true"></i><span class="pledge-payment-order-num">${orderNum}</span></span>`
        );
    }

    $tr.find('.pledge-pay-campaign').text(pledge.campaign || '');
    $tr.find('.pledge-pay-desc').text(pledge.description ?? '');
    $tr.find('.pledge-pay-date').text(pledge.date_pledged);

    if (isMatanahHold) {
        $tr.find('.pledge-pay-amt-due').append(buildMatanahSaveGroup(pledge.id));
        if (opts.showAmountInput) {
            $tr.find('.pledge-pay-amt-input-wrap').removeClass('d-none').append(
                buildAmountInput({ id: pledge.id, value: 0, max: use_amount, min: getKioskConfig().minTransAmt, disabled: true })
            );
        }
    } else {
        $tr.find('.pledge-pay-amt-due').text(`$${use_amount}`);
        if (opts.showAmountInput) {
            $tr.find('.pledge-pay-amt-input-wrap').removeClass('d-none').append(
                buildAmountInput({ id: pledge.id, value: amountToPay, max: use_amount })
            );
        }
    }

    return { $el: $tr, use_amount, isMatanahHold };
}

function isForceSurchargePledge(pledge) {
    return Number(pledge.force_surcharge) === 1;
}

function trackSurcharge(pledge, accumulator) {
    if (isForceSurchargePledge(pledge)) {
        accumulator.forceSurchargeFound = true;
        accumulator.forceCampaignsArr.push(pledge.campaign);
        return;
    }
    accumulator.optionSurchargeFound = true;
    accumulator.optionCampaignsArr.push(pledge.campaign);
}

function buildRecurringSinglePaymentTable(pledges) {
    const sorted = sortPledges(pledges);
    const showOrderColumn = sorted.length > 1;
    const $table = cloneTemplate('tpl-pledge-payment-table');
    if (showOrderColumn) {
        $table.addClass('table-draggable');
        $table.find('.pledge-payment-th-order').removeClass('d-none');
    }

    const $tbody = $table.find('tbody');
    const folded = foldPledgeTableRows(sorted, (pledge, stats, index) => {
        const { $el } = buildPledgeRow(pledge, {
            showOrder: showOrderColumn,
            orderIndex: index,
        });
        $tbody.append($el);
    });

    if (showOrderColumn) {
        const colSpan = 4;
        const tfoot = document.createElement('tfoot');
        const tr = document.createElement('tr');
        const tdLabel = document.createElement('td');
        tdLabel.colSpan = colSpan;
        tdLabel.innerHTML = '<strong>Totals</strong>';
        tr.appendChild(tdLabel);
        const tdTotal = document.createElement('td');
        tdTotal.id = 'matanah_input_tot';
        tdTotal.innerHTML = folded.matanahFound
            ? '<strong>Matanah</strong>'
            : `<strong>$${formatCurrency(folded.totalAmount)}</strong>`;
        tr.appendChild(tdTotal);
        tfoot.appendChild(tr);
        $table.append(tfoot);
    }

    return {
        tableNode: $table[0],
        ...folded,
        showOrderColumn,
    };
}

function buildFlatPaymentTable(pledges) {
    const sorted = sortPledges(pledges);
    const $table = cloneTemplate('tpl-pledge-payment-table');
    $table.find('.pledge-payment-th-amount-pay').removeClass('d-none');
    const $tbody = $table.find('tbody');

    const folded = foldPledgeTableRows(sorted, (pledge) => {
        const { $el } = buildPledgeRow(pledge, { showAmountInput: true });
        $tbody.append($el);
    });

    return {
        tableNode: $table[0],
        ...folded,
        showOrderColumn: false,
    };
}

function buildGatewaySection(gid, groupPledges, opts = {}) {
    const $section = cloneTemplate('tpl-gateway-section');
    $section.attr('data-gateway-id', gid);

    const first = groupPledges[0];
    const label = String(first.gateway_title || '').trim() || 'Credit';
    $section.find('.mgr-pledge-gateway-name').text(
        getPledgePayCongregationGatewayCount() === 1 ? '' : ` for ${label}`
    );
    const countLabel = `${groupPledges.length} ${groupPledges.length === 1 ? 'pledge' : 'pledges'}`;
    $section.find('.mgr-pledge-gateway-toggle-closed').text(`Show details for ${countLabel}`);
    $section.find('.mgr-pledge-gateway-toggle-open').text(`Hide details for ${countLabel}`);
    const detailsId = `mgr_gateway_pledge_details_${gid}`;
    $section.find('.mgr-pledge-gateway-body').attr('id', detailsId);
    const toggle = $section.find('.mgr-pledge-gateway-toggle')[0];
    toggle.setAttribute('aria-controls', detailsId);
    toggle.addEventListener('click', () => {
        setPledgePayDetailsExpanded($section[0], toggle.getAttribute('aria-expanded') !== 'true');
    });

    const $table = $section.find('.mgr-pledge-gateway-payment-table');
    $table.attr('data-gateway-id', gid);

    if (opts.showSelect) {
        $table.find('.pledge-payment-th-select').removeClass('d-none');
    }

    const $tbody = $table.find('tbody');
    groupPledges.forEach((pledge) => {
        const { $el, isMatanahHold } = buildPledgeRow(pledge, {
            gatewayId: gid,
            showSelect: opts.showSelect,
            showAmountInput: true,
        });
        $tbody.append($el);
        if (isMatanahHold || opts.showSelect) {
            setPledgePayDetailsExpanded($section[0], true);
        }
    });

    if (gid > 0) {
        const creditId = `mgr_gateway_use_credit_${gid}`;
        const $panel = $section.find('.mgr-gateway-credit-panel');
        $panel.attr('data-gateway-id', gid);
        const $cb = $panel.find('.mgr-gateway-use-credit');
        $cb.attr({ id: creditId, 'data-gateway-id': gid });
        $panel.find('.account-credit__apply').attr('for', creditId);
        $panel.find('.mgr-gateway-credit-apply').attr({ 'data-gateway-id': gid, id: `${creditId}_amount` });
        $panel.find('.account-credit__editor-label').attr('for', `${creditId}_amount`);
        $panel.find('.mgr-gateway-credit-available').attr('data-gateway-id', gid);
    } else {
        $section.find('.mgr-gateway-credit-panel').replaceWith(cloneTemplate('tpl-gateway-no-credit-note'));
    }

    return $section;
}

function buildManagerGroupedPaymentTable(pledges, showSelectColumn) {
    const sorted = sortPledges(pledges);
    const folded = foldPledgeTableRows(sorted, () => {});

    const groupMap = new Map();
    sorted.forEach((pledge) => {
        const gid = Number(pledge.gateway_id) || 0;
        if (!groupMap.has(gid)) groupMap.set(gid, []);
        groupMap.get(gid).push(pledge);
    });

    const groupIds = [...groupMap.keys()].sort((a, b) => {
        const minA = Math.min(...groupMap.get(a).map((p) => parsePledgeDate(p)?.getTime() ?? 0));
        const minB = Math.min(...groupMap.get(b).map((p) => parsePledgeDate(p)?.getTime() ?? 0));
        return minA - minB || a - b;
    });

    const $stack = cloneTemplate('tpl-gateway-stack');

    groupIds.forEach((gid) => {
        $stack.append(buildGatewaySection(gid, groupMap.get(gid), {
            showSelect: showSelectColumn,
        }));
    });

    return {
        tableNode: $stack[0],
        ...folded,
        showOrderColumn: false,
    };
}

function buildTableForLayout(layout, pledges, profile) {
    if (layout === 'recurring_draggable') {
        return buildRecurringSinglePaymentTable(pledges);
    }
    if (layout === 'flat_amount_inputs') {
        return buildFlatPaymentTable(pledges);
    }
    return buildManagerGroupedPaymentTable(pledges, profile.showSelectColumn);
}

function applyPledgePayTableSurchargeUi($container, result, profile) {
    const percent = (getPledgePaySurchargeAmt() * 100) + '%';
    $container.find('#cover_surcharge_row')
        .hide()
        .data('mgrOptionalSurchargePanelActive', false)
        .data('mgrRequiredSurchargeActive', false);
    $container.find('#mgr_pledge_optional_surcharge_panel, #mgr_pledge_required_surcharge_panel, #force_surcharge_row').hide();
    $container.find('#cover_surcharge')
        .data('mgrSurchargeMatanahLock', !!result.matanahFound)
        .prop({
            checked: false,
            disabled: !!result.matanahFound,
        });
    $container.find('#surcharge_message').html('');

    if ($container.find('.mgr-pledge-gateway-stack').length) {
        syncGatewaySurchargePanels($container);
        return;
    }

    if (profile.isMarkPaid) {
        const $modalRoot = $container.closest('#pledge_pay_body, #setPledgePaymentModal');
        syncPledgePayModalMode($modalRoot, 'mark');
        $container.find('#force_surcharge_message').html('');
        return;
    }

    const surchargeRate = getPledgePaySurchargeAmt();
    if (Number.isFinite(surchargeRate) && surchargeRate > 0 && result.optionSurchargeFound) {
        const cleaned = [...new Set(result.optionCampaignsArr)];
        const campPhrase = formatCampaignNounPhrase(cleaned);
        if (profile.isMgrStylePayUi) {
            const pctLabel = formatSurchargePercent();
            $container.find('#surcharge_message').html(`Cover the ${pctLabel}% processing fee for the ${campPhrase}.`);
        } else {
            $container.find('#surcharge_message').html(
                `I would like to cover the cost of processing for the ${campPhrase} by adding ${percent}`
            );
        }
        $container.find('#cover_surcharge_row').data('mgrOptionalSurchargePanelActive', true);
    }
    pledgeUpdateSurchargeSectionRowVisibility($container);
    updateForceSurchargeRow($container, result);
}

function applyPledgePayTableKeyboard($tableDiv) {
    if (!isKioskVirtualKeyboardEnabled()) {
        $tableDiv.find('.matanah_amt_entry').prop({ type: 'number' });
        return;
    }
    const refreshKioskActivity = typeof window.updateTimer === 'function'
        ? window.updateTimer
        : function () {
            window.user_active = true;
            window.last_activity = Date.now();
        };
    const amountKbOptions = {
        visible: refreshKioskActivity,
        keyboardChange: refreshKioskActivity,
        beforeClose: refreshKioskActivity,
    };
    attachAmountVirtualKeyboard($tableDiv.find('.matanah_amt_entry'), amountKbOptions);
    attachAmountVirtualKeyboard($tableDiv.find('.mgr-gateway-credit-apply'), amountKbOptions);
    initVirtualKeyboard();
}

function applyPledgePayTableMgrSummary($container, profile) {
    if (!profile.isMgrStylePayUi) {
        return;
    }
    const $summaryRoot = $container.closest('#pledge_pay_body');
    if (!$summaryRoot.length) {
        return;
    }
    const modeIsRecurring = $summaryRoot.find('input[name="payment_mode"]:checked').val() === 'recurring';
    mgrPledgeModalSyncSummaryVisibility($summaryRoot);
    pledgePayBillSyncSummaryColumnLayout($summaryRoot, modeIsRecurring);
    pledgePayBillUpdateSummary($summaryRoot);
    mgrPledgeModalSyncPaymentTableFooter($summaryRoot);
    updatePledgePayNowButtonEnabledState($summaryRoot);
}

function renderPledgePaymentTable(pledges, isRecurringMode, $pledgePayRoot, tableContext) {
    const $container = $pledgePayRoot.first();
    const $tableDiv = $container.find('#set_pledge_payment_table').first();
    if (!$tableDiv.length) {
        return null;
    }

    const resolvedContext = tableContext || buildPledgePayTableContext($container, null, isRecurringMode);
    const profile = resolvePledgePayTableProfile(resolvedContext, pledges);
    const layout = profile.layout === 'recurring_draggable' || isRecurringMode
        ? 'recurring_draggable'
        : profile.layout;
    const result = buildTableForLayout(layout, pledges, profile);

    $tableDiv.empty().append(result.tableNode);
    syncPledgePayGatewayTotals($container);
    applyPledgePayTableSurchargeUi($container, result, profile);
    $container.find('#matanah_ct').val(result.matanahCount);
    applyPledgePayTableKeyboard($tableDiv);
    updateStatementPledgeSelectionValidation($container);
    applyPledgePayTableMgrSummary($container, profile);

    return result;
}

function pledgePayModalBody($modal) {
    const $root = $modal && $modal.length ? $modal : $('#setPledgePaymentModal');
    return $root.find('#pledge_pay_body').length ? $root.find('#pledge_pay_body') : $root;
}

function syncPledgePayModalMode($modal, payType) {
    const $body = pledgePayModalBody($modal);
    $body.find('.payment_type').show();
    $body.find('.mark_paid_div').hide().addClass('pledge-pay-hidden');
    return $body;
}

export {
    syncPledgePayModalMode,
    renderPledgePaymentTable,
    buildRecurringSinglePaymentTable,
    buildManagerGroupedPaymentTable,
    pledgePayModalBody,
};
